import { Component } from '@theme/component';

const STAR_SIZE = 'var(--font-size--2xs)';
const RATING_VALUE_PATTERN = /^\s*(\d+(?:[.,]\d+)?)/;

/**
 * Restores the Figma star size after Bazaarvoice injects an inline-important
 * 17px width and height. Bazaarvoice's polygon is the same vector as the Ceres
 * asset, and its generated gradient preserves the live fractional rating.
 */
export class BazaarvoiceRatingStars extends HTMLElement {
  #observer = new MutationObserver(() => this.#syncStars());

  connectedCallback() {
    this.#observer.observe(this, { childList: true, subtree: true });
    this.#syncStars();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
  }

  #syncStars() {
    this.querySelectorAll('.bv_stars_svg_no_wrap > svg').forEach((star) => {
      star.style.setProperty('width', STAR_SIZE, 'important');
      star.style.setProperty('height', STAR_SIZE, 'important');
    });

    if (!this.hasAttribute('data-compact')) return;

    const value = this.querySelector('[data-bv-rating-value]');
    const accessibleRating = this.querySelector('.bv-off-screen')?.textContent;
    const rating = accessibleRating?.match(RATING_VALUE_PATTERN)?.[1];
    if (!(value instanceof HTMLElement) || !rating) return;

    value.hidden = false;
    if (value.textContent !== rating) value.textContent = rating;
  }
}

/**
 * Defers the Bazaarvoice reviews container until the drawer holding it first
 * opens.
 *
 * The reviews panel lives inside a native `<dialog>`, so it is `display: none`
 * until opened. Bazaarvoice renders into hidden containers, but the features
 * that depend on measuring themselves — photo galleries, pagination, and the
 * impression events that decide whether a review counts as seen — behave badly
 * when they initialise against a zero-size box.
 *
 * Inserting the container on first open sidesteps all of it. The Bazaarvoice
 * loader keeps a MutationObserver over `[data-bv-show]` for the life of the
 * page, so an element added later renders on its own; there is no API to call.
 *
 * @typedef {object} BazaarvoiceReviewsRefs
 * @property {HTMLElement} mount - Element the Bazaarvoice container replaces.
 *
 * @extends {Component<BazaarvoiceReviewsRefs>}
 *
 * @property {string[]} requiredRefs - Required refs: 'mount'
 */
export class BazaarvoiceReviews extends Component {
  requiredRefs = ['mount'];

  #paginationObserver = new MutationObserver(() => this.#syncPaginationIcons());

  /** @type {boolean} */
  #mounted = false;

  /** @type {HTMLElement | null} */
  #drawer = null;

  #handleDrawerOpen = () => this.#mount();

  connectedCallback() {
    super.connectedCallback();

    if (this.querySelector('template[data-bv-pagination-icon]')) {
      this.#paginationObserver.observe(this, { childList: true, subtree: true });
      this.#syncPaginationIcons();
    }

    this.#drawer = this.closest('theme-drawer');

    // Rendered outside a drawer — nothing to wait for.
    if (!this.#drawer) {
      this.#mount();
      return;
    }

    this.#drawer.addEventListener('theme-drawer:open', this.#handleDrawerOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#paginationObserver.disconnect();
    this.#drawer?.removeEventListener('theme-drawer:open', this.#handleDrawerOpen);
  }

  /** Reuse the carousel assets after each vendor pagination render. */
  #syncPaginationIcons() {
    for (const direction of ['prev', 'next']) {
      const template = this.querySelector(`template[data-bv-pagination-icon="${direction}"]`);
      const target = this.querySelector(`#reviews_container + div .${direction} > div[aria-hidden="true"]`);
      if (!(template instanceof HTMLTemplateElement) || !target || target.querySelector('[data-bv-carousel-chevron]')) {
        continue;
      }

      const icon = template.content.querySelector('svg')?.cloneNode(true);
      if (!(icon instanceof SVGElement)) continue;
      icon.setAttribute('data-bv-carousel-chevron', '');
      target.append(icon);
    }
  }

  /**
   * Swaps the placeholder for a Bazaarvoice container. Runs at most once.
   */
  #mount() {
    if (this.#mounted) return;

    const productId = this.dataset.bvProductId;
    if (!productId) return;

    this.#mounted = true;

    const container = document.createElement('div');
    container.setAttribute('data-bv-show', 'reviews');
    container.setAttribute('data-bv-product-id', productId);

    this.refs.mount.replaceChildren(container);
    this.#drawer?.removeEventListener('theme-drawer:open', this.#handleDrawerOpen);
  }
}

if (!customElements.get('bazaarvoice-reviews')) {
  customElements.define('bazaarvoice-reviews', BazaarvoiceReviews);
}

if (!customElements.get('bazaarvoice-rating-stars')) {
  customElements.define('bazaarvoice-rating-stars', BazaarvoiceRatingStars);
}
